import {
  BugReporter,
  isReporterVisible,
  type ReporterUser,
} from "~/shared/bug-reporter";
import { ENV } from "~/config/env.config";
import { isAutomatedBrowser } from "~/lib/automation";
import { useIsDemoOrganization } from "~/hooks/useIsDemoOrganization";
import {
  useAuthStore,
  getAuthToken,
} from "~/store/authentication/useAuthStore";

/**
 * Admin-side integration glue for the canonical bug reporter widget.
 *
 * This file is Admin-owned and is NOT part of the synced canonical at
 * `app/shared/bug-reporter/` — it must never be overwritten by a re-sync.
 * It wires Admin's auth store, API base URL, and the environment-based
 * visibility gate to the canonical widget's props. See
 * `app/shared/bug-reporter/SYNC.md`.
 */
export function BugReporterMount() {
  const userId = useAuthStore((s) => s.userId);
  const email = useAuthStore((s) => s.email);
  const profile = useAuthStore((s) => s.profile);

  // Identify the reporter from the auth store when authenticated; otherwise
  // submit anonymously (the widget renders fine with `user = null`).
  const id = userId ?? profile?.id ?? null;
  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ");
  const user: ReporterUser | null = id
    ? {
        id,
        email: email ?? profile?.email ?? undefined,
        name: fullName || undefined,
      }
    : null;

  // Admin's production AND staging builds both report
  // `import.meta.env.MODE === "production"`; the two are distinguished by
  // `VITE_ENVIRONMENT` (see env.config.ts — also what Sentry keys off). So we
  // can't rely on the canonical's MODE-only gate to show the widget on staging.
  // Force-enable in dev + staging; in production pass `undefined` so the
  // canonical's `VITE_ENABLE_BUG_REPORTER` flag still works as a debug escape
  // hatch (scope-decisions.md D4). Net: visible in dev/staging, hidden in prod.
  //
  // …except in the protected demo organization: demos run on staging, and a
  // floating "Report a bug" button over every screen is not something to show
  // a prospect (2026-10-20 demo-readiness audit).
  const isDemoOrganization = useIsDemoOrganization();
  const enabled = isDemoOrganization
    ? false
    : ENV.ENVIRONMENT !== "production"
      ? true
      : undefined;

  // Local dev only: the dev widgets (the Agentation panel in root.tsx and the TanStack Query
  // Devtools badge in query.provider.tsx) also pin to the bottom-right corner and paint over the
  // reporter button. Lift the button above them so both are reachable. Both render only under
  // exactly `import.meta.env.DEV && !isAutomatedBrowser()`, which is this condition, so it has no
  // effect in staging/production or under Playwright: with nothing in the corner, the canonical
  // button keeps its bottom-right position there.
  const liftAboveDevWidget = import.meta.env.DEV && !isAutomatedBrowser();

  // P-22 (d6 decision 1): a bar pinned to the bottom of the page keeps its
  // action clear of the button only while the button is actually there. The
  // same inputs the canonical widget decides its own visibility from, so the
  // two cannot disagree. Lifted above the dev widget (local dev only), the
  // button no longer sits on the bars, so they need no clearance either.
  const isReporterOnScreen = isReporterVisible({
    enabledOverride: enabled,
    mode: import.meta.env.MODE,
    flagValue: import.meta.env.VITE_ENABLE_BUG_REPORTER,
  });
  const needsBottomBarClearance = isReporterOnScreen && !liftAboveDevWidget;

  return (
    <>
      {liftAboveDevWidget && (
        <style>{`[data-testid="bug-reporter-button"]{bottom:5rem;}`}</style>
      )}
      {needsBottomBarClearance && (
        // The button is 40px wide, 16px in from the edge: 72px leaves a 16px
        // gap before a bar's last action (consumed by `.px-bottom-bar`).
        <style>{`:root{--bug-reporter-clearance:4.5rem;}`}</style>
      )}
      <BugReporter
        appName="Admin"
        apiBaseUrl={ENV.API_BASE_URL}
        user={user}
        enabled={enabled}
        getAuthToken={getAuthToken}
      />
    </>
  );
}
